import React from 'react';
import type { ExperienceItem, Bullet } from '../../data/cv';
import { Bullets } from './Bullets';
import { CompanyMark } from './CompanyMark';
import { TechStack, InlineStack } from './TechStack';
import { periodEndpoints, splitLocation, roleDuration } from './helpers';
interface Props {
  item: ExperienceItem;
  isLast: boolean;
  /** One-page mode: trims summary/bullets/stack for a tighter footprint. */
  compact?: boolean;
}
/**
 * Editorial experience entry: a left "spine" carrying the index + year range,
 * and a right content well. On print each entry stays intact.
 */
export function ExperienceEntry({
  item,
  isLast,
  compact = false
}: Props) {
  const {
    start,
    end,
    same
  } = periodEndpoints(item.period);
  const {
    place,
    mode
  } = splitLocation(item.location);
  const duration = roleDuration(item.period);
  // In compact mode, flatten bullets (incl. subsections) and cap the count so
  // five detailed roles + the intro + tail fit within a 3-page budget.
  const compactBullets: Bullet[] = compact ? [...(item.bullets ?? []), ...(item.subsections?.flatMap(s => s.bullets) ?? [])].slice(0, 2) : [];
  return <article className="cv-entry group relative grid grid-cols-[82px_1fr] gap-6" data-id="element-107" data-mp="1bxmm5l4btdqv">
      {/* spine: month/year range (clearly stacked) + duration */}
      <div className="flex flex-col items-end pt-[2px] text-right" data-id="element-108" data-mp="1kpy7wtohwd0i">
        <span className="whitespace-nowrap font-mono text-[11px] font-medium tabular-nums leading-none text-[var(--ink)]" data-id="element-109" data-mp="1pormyqjfkc2s">
          {end}
        </span>
        {!same && <span className="mt-1 whitespace-nowrap font-mono text-[10px] tabular-nums leading-none text-[var(--ink-faint)]" data-id="element-110" data-mp="1um1b82v32hp1">
            {start}
          </span>}
        {duration && <span className="mt-2 rounded-full bg-[var(--accent)]/10 px-1.5 py-[1px] font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-111" data-mp="23nrb7x316oyf">
            {duration}
          </span>}
      </div>

      {/* content */}
      <div className={isLast ? 'pb-0' : compact ? 'pb-4' : 'pb-8'} data-id="element-112" data-mp="1cl6xe4ssjlx1">
        {/* header: monogram + company / role, with meta on the right */}
        <div className="keep-with-next flex items-start gap-3.5" data-id="element-113" data-mp="17nnhz1kcx33i">
          <CompanyMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} data-id="element-114" data-mp="1lmmwhge5o1ly" />

          <div className="min-w-0 flex-1" data-id="element-115" data-mp="1hmmjm06pfyh0">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5" data-id="element-116" data-mp="1vjj7ptvfv1u7">
              <h3 className="font-display text-[19px] font-medium leading-tight text-[var(--ink)]" data-id="element-117" data-mp="1qlq3v7hlyjx3">
                {item.company}
              </h3>
              <div className="flex items-center gap-2.5" data-id="element-118" data-mp="259b7r3mb4n62">
                {item.industry && <span className="rounded-full bg-[var(--accent)]/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]" data-id="element-119" data-mp="20bt33pfbou3v">
                    {item.industry}
                  </span>}
                {mode && <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-faint)]" data-id="element-120" data-mp="2syj3h6puaepq">
                    {mode}
                  </span>}
              </div>
            </div>

            <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-soft)]" data-id="element-121" data-mp="2jw1iswpu1u4u">
              {item.title}
              {place && <span className="ml-2 font-normal normal-case tracking-normal text-[var(--ink-faint)]" data-id="element-122" data-mp="2a6hxlkendo6t">
                  · {place}
                </span>}
            </p>
          </div>
        </div>

        {/* body hangs under the header text, aligned past the monogram */}
        <div className="pl-[58px]" data-id="element-123" data-mp="257neehcrpxm6">
          {/* COMPACT: only the first summary sentence + up to 3 flat bullets. */}
          {compact ? <>
              {item.summary?.[0] && <p className="cv-clamp-3 mt-1.5 max-w-[80ch] text-[11.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-124" data-mp="1w6z0rnradz9t">
                  {item.summary[0]}
                </p>}
              {compactBullets.length > 0 && <div className="mt-1.5 max-w-[80ch] gap-x-10 sm:columns-2" data-id="element-125" data-mp="1j8r79mp1gq5e">
                  <Bullets items={compactBullets} data-id="element-126" data-mp="1efy2iytklhtb" />
                </div>}
              {item.stack && item.stack.length > 0 && <div className="mt-2" data-id="element-127" data-mp="1aew3oj3kf7rv">
                  <InlineStack stack={item.stack} data-id="element-128" data-mp="15frptf5p4lv7" />
                </div>}
            </> : <>
              {item.summary && <div className="mt-3 max-w-[64ch] space-y-2" data-id="element-129" data-mp="10jb56uxpgpmv">
                  {item.summary.map((p, i) => <p key={i} className="text-[12.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-130" data-mp="rga08xkkt4ks" data-mpi={i}>
                      {p}
                    </p>)}
                </div>}

              {item.bullets && <div className="mt-3.5 max-w-[68ch] gap-x-10 sm:columns-2" data-id="element-131" data-mp="7mjvex7jukx3">
                  <Bullets items={item.bullets} data-id="element-132" data-mp="7kfpyau05dup" />
                </div>}

              {item.subsections && <div className="mt-4 gap-x-10 sm:columns-2" data-id="element-133" data-mp="hcliblo61xaf">
                  {item.subsections.map((sub, i) => <div key={i} className="mb-4 break-inside-avoid last:mb-0" data-id="element-134" data-mp="le6oko673dbn" data-mpi={i}>
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]" data-id="element-135" data-mp="3ktbs8bmvcuhe">
                        {sub.heading}
                      </p>
                      <Bullets items={sub.bullets} data-id="element-136" data-mp="3ptzszp0wgpot" />
                    </div>)}
                </div>}

              {/* tech stack — grouped, icon-bearing, consistent across roles */}
              {item.stack && item.stack.length > 0 && <TechStack stack={item.stack} data-id="element-137" data-mp="192zmuraahx9f" />}
            </>}
        </div>
      </div>
    </article>;
}